import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useRevit } from '../../contexts/RevitContext';
import { useAuth } from '@clerk/clerk-react';
import { generateRevtrackLink, extractVideoId } from '../../utils/linkUtils';
import './SplitTests.css';
import Icon from '../Icon';

const SplitTests = () => {
    const [offers, setOffers] = useState([]);
    const [generatedCodes, setGeneratedCodes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [splitTests, setSplitTests] = useState([]);
    const [copiedCode, setCopiedCode] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [newSplitTest, setNewSplitTest] = useState({
        name: '',
        offerName: '',
        sourceType: 'ad',
        resourceName: '',
        pages: [
            { url: '' },
            { url: '' }
        ]
    });
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [splitTestToDelete, setSplitTestToDelete] = useState(null);

    const { username } = useRevit();
    const { getToken } = useAuth();
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    useEffect(() => {
        fetchOffers();
        fetchSplitTests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOffers = async () => {
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/offers/${username}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setOffers(response.data.offers || []);
        } catch (error) {
            console.error('Error fetching offers:', error);
        }
    };

    const fetchSplitTests = async () => {
        setIsLoading(true);
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/split-tests/${username}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            setSplitTests((response.data.splitTests || []).map(test => ({
                ...test,
                total_clicks: parseInt(test.total_clicks) || 0,
                total_sales: parseInt(test.total_sales) || 0,
                pages: test.pages?.map(page => ({
                    ...page,
                    clicks: parseInt(page.clicks) || 0,
                    sales: parseInt(page.sales) || 0
                })) || []
            })));
        } catch (error) {
            console.error('Error fetching split tests:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateSplitTest = async () => {
        if (!newSplitTest.name || !newSplitTest.offerName || !newSplitTest.resourceName) {
            alert('Please fill in all required fields');
            return;
        }

        if (newSplitTest.pages.some(page => !page.url)) {
            alert('Please provide URLs for all pages');
            return;
        }

        setIsCreating(true);
        try {
            const token = await getToken();
            const response = await axios.post(`https://${SERVER_URL}/api/create-split-test`, {
                username,
                offerName: newSplitTest.offerName,
                splitTestName: newSplitTest.name,
                resource: {
                    type: newSplitTest.sourceType,
                    name: newSplitTest.resourceName
                },
                pages: newSplitTest.pages
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {                
                // Generate tracking links for the modal using the returned page IDs
                const links = [];
                newSplitTest.pages.forEach((page, index) => {
                    let resourceValue = newSplitTest.resourceName;
                    if (newSplitTest.sourceType === 'youtube' || newSplitTest.sourceType === 'pinComment') {
                        resourceValue = extractVideoId(newSplitTest.resourceName) || newSplitTest.resourceName;
                    }
                    
                    const pageId = response.data.pageIds[index];
                    const trackingLink = generateRevtrackLink(
                        page.url, 
                        newSplitTest.sourceType, 
                        resourceValue, 
                        pageId
                    );
                    
                    links.push({
                        variant: pageId,
                        pageName: `Page ${index + 1}`,
                        code: trackingLink
                    });
                });
                
                setGeneratedCodes(links);
                setShowModal(true);
                
                setNewSplitTest({
                    name: '',
                    offerName: '',
                    sourceType: 'ad',
                    resourceName: '',
                    pages: [{ url: '' }, { url: '' }]
                });
                fetchSplitTests(); // Refresh the list
            }
        } catch (error) {
            console.error('Error creating split test:', error);
            alert('Error creating split test');
        } finally {
            setIsCreating(false);
        }
    };

    const copyToClipboard = (code, index) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(index);
        setTimeout(() => setCopiedCode(''), 2000);
    };

    const handleDeleteSplitTest = async () => {
        if (!splitTestToDelete) return;
        
        try {
            const token = await getToken();
            const response = await axios.delete(
                `https://${SERVER_URL}/api/delete-split-test/${username}/${splitTestToDelete.id}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            if (response.status === 200) {
                setSplitTests(prevTests => prevTests.filter(test => test.id !== splitTestToDelete.id));
                setShowDeleteConfirmation(false);
                setSplitTestToDelete(null);
            }
        } catch (error) {
            console.error('Error deleting split test:', error);
            alert('Error deleting split test');
        }
    };

    return (
        <div className="split-tests-container">
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-text">Loading split tests...</div>
                </div>
            )}

            {isCreating && (
                <div className="loading-overlay">
                    <div className="loading-text">Creating split test...</div>
                </div>
            )}

            <div className="youtube-account-container">
                <p className="body-large-semibold">Split Tests</p>
            </div>
            
            <div className="create-split-test">
                <h3>Create New Split Test</h3>
                <div className="split-test-form">
                    <div className="split-test-form-group">
                        <label htmlFor="splitTestName">Split Test Name:</label>
                        <input
                            type="text"
                            id="splitTestName"
                            value={newSplitTest.name}
                            onChange={(e) => setNewSplitTest({...newSplitTest, name: e.target.value})}
                            placeholder="Enter split test name"
                            required
                        />
                    </div>

                    <div className="split-test-form-group">
                        <label htmlFor="selectedOffer">Select Offer:</label>
                        <select
                            id="selectedOffer"
                            value={newSplitTest.offerName}
                            onChange={(e) => setNewSplitTest({...newSplitTest, offerName: e.target.value})}
                            required
                        >
                            <option value="">Choose an offer...</option>
                            {offers.map(offer => (
                                <option key={offer.id} value={offer.name}>
                                    {offer.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="split-test-form-group">
                        <label htmlFor="sourceType">Where will you use this split test?</label>
                        <select
                            id="sourceType"
                            value={newSplitTest.sourceType}
                            onChange={(e) => setNewSplitTest({...newSplitTest, sourceType: e.target.value})}
                            required
                        >
                            <option value="ad">Ad</option>
                            <option value="youtube">YouTube Video</option>
                            <option value="email">Email</option>
                            <option value="community">Community Wall</option>
                            <option value="instagram">Instagram</option>
                            <option value="twitter">Twitter</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="podcast">Podcast</option>
                            <option value="blog">Blog</option>
                            <option value="website">Website</option>
                            <option value="tiktok">TikTok</option>
                            <option value="telegram">Telegram</option>
                            <option value="facebook">Facebook</option>
                            <option value="discord">Discord</option>
                            <option value="pinComment">Pinned Comment</option>
                        </select>
                    </div>

                    <div className="split-test-form-group">
                        <label htmlFor="resourceName">
                            {newSplitTest.sourceType === 'youtube' || newSplitTest.sourceType === 'pinComment' 
                                ? `Enter the video URL of the ${newSplitTest.sourceType === 'pinComment' ? 'video' : 'video'} where the link will be placed:`
                                : `Enter the name of the ${newSplitTest.sourceType} where the link will be placed:`
                            }
                        </label>
                        <input
                            type="text"
                            id="resourceName"
                            value={newSplitTest.resourceName}
                            onChange={(e) => setNewSplitTest({...newSplitTest, resourceName: e.target.value})}
                            placeholder="Enter resource name (e.g., video title, email subject)"
                            required
                        />
                    </div>
                </div>

                {/* Page URLs section */}
                <div className="split-test-pages-section">
                    <h4>Page URLs for Split Test</h4>
                    <div className="split-test-pages-grid">
                        {newSplitTest.pages.map((page, index) => (
                            <div key={index} className="split-test-page-form-group">
                                <label htmlFor={`pageUrl${index}`}>Page {index + 1} URL:</label>
                                <input
                                    type="url"
                                    id={`pageUrl${index}`}
                                    value={page.url}
                                    onChange={(e) => {
                                        const updatedPages = [...newSplitTest.pages];
                                        updatedPages[index].url = e.target.value;
                                        setNewSplitTest({...newSplitTest, pages: updatedPages});
                                    }}
                                    placeholder={`Enter URL for page ${index + 1}`}
                                    required
                                />
                            </div>
                        ))}
                    </div>
                    <div className="split-test-note">
                        <strong>Note:</strong> {newSplitTest.offerName 
                            ? `The tracker code for offer "${newSplitTest.offerName}" must be placed on each of these pages.`
                            : 'The tracker code must be placed on each of these pages.'
                        }
                    </div>
                </div>

                <button 
                    type="submit" 
                    className="split-test-create-btn"
                    disabled={!newSplitTest.name || !newSplitTest.offerName || !newSplitTest.resourceName || newSplitTest.pages.some(page => !page.url.trim())}
                    onClick={handleCreateSplitTest}
                >
                    Create Split Test
                </button>
            </div>

            <div className="existing-split-tests">
                <h3>Existing Split Tests</h3>
                {splitTests.length === 0 ? (
                    <p>No split tests created yet.</p>
                ) : (
                    <div className="split-tests-list">
                        {splitTests.map(test => (
                            <div key={test.id} className="split-test-card">
                                <div className="split-test-header">
                                    <h4>{test.name}</h4>
                                    <div className="split-test-header-actions">
                                        <span className="offer-badge">{test.offer_name}</span>
                                        <button 
                                            className="delete-split-test-btn"
                                            onClick={() => {
                                                setSplitTestToDelete(test);
                                                setShowDeleteConfirmation(true);
                                            }}
                                        >
                                            <Icon name="delete" size={20}  color="var(--Delete-Red)" />
                                        </button>
                                    </div>
                                </div>
                                <p>Created: {new Date(test.created_at).toLocaleDateString()}</p>

                                <div className="pages-breakdown">
                                    <h5>Page Performance</h5>
                                    
                                    {/* Legend */}
                                    <div className="performance-legend">
                                        <div className="legend-item">
                                            <div className="legend-color" style={{ backgroundColor: '#4F46E5' }}></div>
                                            <span>{test.pages[0]?.page_url || 'Page 1'}</span>
                                        </div>
                                        <div className="legend-item">
                                            <div className="legend-color" style={{ backgroundColor: '#10B981' }}></div>
                                            <span>{test.pages[1]?.page_url || 'Page 2'}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Clicks Performance Slider */}
                                    <div className="performance-slider-section">
                                        <h6>Clicks: {test.total_clicks || 0}</h6>
                                        <div className="performance-slider">
                                            {/* Page 1 */}
                                            <div 
                                                className="slider-segment"
                                                style={{
                                                    width: `${test.total_clicks > 0 ? ((test.pages[0]?.clicks || 0) / test.total_clicks) * 100 : 50}%`,
                                                    backgroundColor: '#4F46E5'
                                                }}
                                                title={`Page 1: ${test.pages[0]?.clicks || 0} clicks`}
                                            >
                                                <span className="slider-label">{test.pages[0]?.clicks || 0}</span>
                                            </div>
                                            {/* Page 2 */}
                                            <div 
                                                className="slider-segment"
                                                style={{
                                                    width: `${test.total_clicks > 0 ? ((test.pages[1]?.clicks || 0) / test.total_clicks) * 100 : 50}%`,
                                                    backgroundColor: '#10B981'
                                                }}
                                                title={`Page 2: ${test.pages[1]?.clicks || 0} clicks`}
                                            >
                                                <span className="slider-label">{test.pages[1]?.clicks || 0}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sales Performance Slider */}
                                    <div className="performance-slider-section">
                                        <h6>Sales: {test.total_sales || 0}</h6>
                                        <div className="performance-slider">
                                            {/* Page 1 */}
                                            <div 
                                                className="slider-segment"
                                                style={{
                                                    width: `${test.total_sales > 0 ? ((test.pages[0]?.sales || 0) / test.total_sales) * 100 : 50}%`,
                                                    backgroundColor: '#4F46E5'
                                                }}
                                                title={`Page 1: ${test.pages[0]?.sales || 0} sales`}
                                            >
                                                <span className="slider-label">{test.pages[0]?.sales || 0}</span>
                                            </div>
                                            {/* Page 2 */}
                                            <div 
                                                className="slider-segment"
                                                style={{
                                                    width: `${test.total_sales > 0 ? ((test.pages[1]?.sales || 0) / test.total_sales) * 100 : 50}%`,
                                                    backgroundColor: '#10B981'
                                                }}
                                                title={`Page 2: ${test.pages[1]?.sales || 0} sales`}
                                            >
                                                <span className="slider-label">{test.pages[1]?.sales || 0}</span>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                                <div className="split-test-actions">
                                    <div className="link-generator-form">
                                        <div className="split-test-copy-form-row">
                                            <div className="split-test-copy-form-group">
                                                <label>Source Type:</label>
                                                <select 
                                                    value={test.resource_type}
                                                    onChange={(e) => {
                                                        const updatedTests = splitTests.map(t => 
                                                            t.id === test.id 
                                                                ? { ...t, resource_type: e.target.value }
                                                                : t
                                                        );
                                                        setSplitTests(updatedTests);
                                                    }}
                                                >
                                                    <option value="ad">Ad</option>
                                                    <option value="youtube">YouTube Video</option>
                                                    <option value="email">Email</option>
                                                    <option value="community">Community Wall</option>
                                                    <option value="instagram">Instagram</option>
                                                    <option value="twitter">Twitter</option>
                                                    <option value="linkedin">LinkedIn</option>
                                                    <option value="podcast">Podcast</option>
                                                    <option value="blog">Blog</option>
                                                    <option value="website">Website</option>
                                                    <option value="tiktok">TikTok</option>
                                                    <option value="telegram">Telegram</option>
                                                    <option value="facebook">Facebook</option>
                                                    <option value="discord">Discord</option>
                                                    <option value="pinComment">Pinned Comment</option>
                                                </select>
                                            </div>
                                            <div className="split-test-copy-form-group">
                                                <label>Resource Name:</label>
                                                <input 
                                                    type="text"
                                                    value={test.resource_name}
                                                    onChange={(e) => {
                                                        const updatedTests = splitTests.map(t => 
                                                            t.id === test.id 
                                                                ? { ...t, resource_name: e.target.value }
                                                                : t
                                                        );
                                                        setSplitTests(updatedTests);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="split-test-copy-form-row">
                                            <div className="split-test-copy-form-group">
                                                <label>Page:</label>
                                                <select 
                                                    value={test.selectedPageIndex || 0}
                                                    onChange={(e) => {
                                                        const updatedTests = splitTests.map(t => 
                                                            t.id === test.id 
                                                                ? { ...t, selectedPageIndex: parseInt(e.target.value) }
                                                                : t
                                                        );
                                                        setSplitTests(updatedTests);
                                                    }}
                                                >
                                                    {test.pages.map((page, index) => (
                                                        <option key={index} value={index}>
                                                            {page.page_url}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button 
                                                className="split-test-copy-link-btn"
                                                onClick={() => {
                                                    const selectedPageIndex = test.selectedPageIndex || 0;
                                                    const selectedPage = test.pages[selectedPageIndex];
                                                    
                                                    let resourceValue = test.resource_name;
                                                    if (test.resource_type === 'youtube' || test.resource_type === 'pinComment') {
                                                        resourceValue = extractVideoId(test.resource_name) || test.resource_name;
                                                    }
                                                    
                                                    const trackingLink = generateRevtrackLink(
                                                        selectedPage.page_url, 
                                                        test.resource_type, 
                                                        resourceValue, 
                                                        selectedPage.page_id
                                                    );
                                                    
                                                    copyToClipboard(trackingLink, `${test.id}-${selectedPageIndex}`);
                                                }}
                                            >
                                                {copiedCode === `${test.id}-${test.selectedPageIndex || 0}` ? 'Copied!' : 'Copy Link'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Split Test Tracking Links</h3>
                        <p>Use these links for your split test:</p>
                        
                        {generatedCodes.map((item, index) => (
                            <div key={index} className="split-test-code-section">
                                <h4>{item.pageName}</h4>
                                <div className="split-test-code-container">
                                    <div className="split-test-link">{item.code}</div>
                                    <button 
                                        onClick={() => copyToClipboard(item.code, index)}
                                        className="split-test-copy-btn"
                                    >
                                        {copiedCode === index ? 'Copied!' : 'Copy Link'}
                                    </button>
                                </div>
                            </div>
                        ))}
                        
                        <button onClick={() => setShowModal(false)} className="close-btn">
                            Close
                        </button>
                    </div>
                </div>
            )}

            {showDeleteConfirmation && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Split Test</h3>
                        <p>Are you sure you want to delete "{splitTestToDelete?.name}"? This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button onClick={() => setShowDeleteConfirmation(false)} className="cancel-btn">
                                Cancel
                            </button>
                            <button onClick={handleDeleteSplitTest} className="delete-btn">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SplitTests;
